import { Component, Inject, Input, Output, EventEmitter } from '@angular/core';

import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { AccesService } from '../services/acces.service';
import { Coactivite } from '../entities/coactivite.entity';
import { CoactiviteDecision } from '../entities/coactivite-decision.constants';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { TypeMaintenanceService } from '../../../planification-maintenance/type-maintenance/services/type-maintenance.service';
import { safeCombineLatest } from 'src/main/angular/app/technical-modules/rxjs-helpers/rxjs-helpers';
import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { AccesControleSecurite } from '../../../../static-types/entities/acces-controle-securite.entity';

export type CoactiviteEolienCheckCommand = {
	accesId: number;
	accesCegex: boolean;
	ouvrageId: number;
	decisionSubject?: Subject<CoactiviteDecision>;
	isUserCc?: boolean | undefined;
	isUserCac?: boolean | undefined;
	controleSecurite?: number | null;
};

@Component({
	selector: 'coactivite-eolien-checker',
	templateUrl: 'coactivite-eolien-checker.html',
	styleUrls: ['coactivite-checker.css'],
})
export class CoactiviteEolienCheckerComponent {
	// Valeurs a binder sur la vue
	popupTitle: string = 'Attention coactivité détectée, voulez-vous valider l\'accès et autoriser la coactivité ?';
	accesId!: number;
	coactivites: Coactivite[] = [];
	
	// Gestion globale de la fenetre
	displaySubject: Subject<boolean> = new Subject<boolean>();
	isCac: boolean | undefined = false;
	isCc: boolean | undefined = false;
	showBoutonDemandeValidation?: boolean | undefined = false;
	
	private decisionSubject: Subject<CoactiviteDecision> | null = null;
	private controleSecurite: number | null = null;
	private controleSecuriteCoactiviteId: number | null = null;
	
	constructor( //
		@Inject(AccesService) accesService: AccesService,
		@Inject(OuvrageService) ouvrageService: OuvrageService,
		@Inject(TypeMaintenanceService) typeMaintenanceService: TypeMaintenanceService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		staticTypeService.getAccesControleSecuriteList().subscribe(
		    list => {
				console.log('controles sécurité:', list.map(i => `${i.id} => ${i.label}`));
		        const coactiviteKo = list.find(
		            item => (item.label ?? '').trim().toLowerCase().startsWith('coactivité ko')
		        );
		        this.controleSecuriteCoactiviteId = coactiviteKo ? coactiviteKo.id : null;
		    }
		);
		// surcharge du titre pour une coactivité eol
		this.popupTitle = 'Attention coactivité détectée, voulez-vous valider l\'accès et autoriser la coactivité ?';
		
		// Charger les types de maintenance pour pouvoir forger leurs libelles sur demande
		let typeMap$ = typeMaintenanceService.getTypeMaintenancesMap();
		
		// Lors de la reception d'une demande de verification de coactivite sur un parc
		let coactivites$ = this.checkCommandsSubject.pipe(
			// Recuperation de tous les acces deja en cours sur l'ouvrage
			mergeMap(
				(command) => accesService.getEnCoursByOuvrage(command.ouvrageId, 2).pipe(
					map(
						(acces) => ({
							 command: command,
							 accesCoactifs: acces
						})
					)
				)
			),
			
			// Pour chaque acces, aller chercher les types d'operation lies
			mergeMap(
				({ command, accesCoactifs }) => {
					// Pour chaque acces coactif, recuperer les operations liees
					let results$ = accesCoactifs.map(
						(acces) => accesService.getOperationsTLRByAcces(acces.id).pipe(
							map(
								(operations) => {
									let typesId = operations.map(operation => operation.acteOnmId);
									return {
										acces: acces,
										typesId: typesId
									};
								}
							)
						)
					);
					
					return forkJoin({
						command: of(command),
						protoCoactivites: safeCombineLatest(results$),
					});
				}
			),
			
			// Pour chaque acces coactif, aller chercher les ouvrages concernes
			mergeMap(
				({ command, protoCoactivites }) => {
					
					let results$ = protoCoactivites.map(
						(protoCoactivite) =>  ouvrageService.getOuvrageConcerneTLRByAcces(protoCoactivite.acces.id).pipe(
							map(
								(ouvragesConcernes) => {
									let nomsOuvrages = ouvragesConcernes //
										.map(ouvrage => ouvrage.nom) //
										.join(",") //
									;
									return {acces: protoCoactivite.acces, nomsOuvrages: nomsOuvrages, typesId: protoCoactivite.typesId};
								}
							)
						)
					);
					
					return forkJoin({
						command: of(command),
						coactivites: safeCombineLatest(results$),
					});
				}
			),
		);
		
		// Une fois qu'on connait les coactivites et qu'on a charge les donnes sur les types de maintenances (actes O&M)
		combineLatest([typeMap$, coactivites$]).pipe(
			// Creer les libelles d'actes
			map(
				([typeMap, { command, coactivites }]) => {
					let newCoactivites = coactivites.map(
						(protoCoactivite) => {
							let acces = protoCoactivite.acces;
							let nomsOuvrages = protoCoactivite.nomsOuvrages;
							let typesId = protoCoactivite.typesId;
							
							let typesLabels: string[] = [];
							for (let id of typesId) {
								let type = typeMap.get(id);
								if (type) {
									typesLabels.push(type.libelle);
								}
							}
							
							let coactivite: Coactivite = {
								acces: acces,
								nomsOuvrages: nomsOuvrages,
								typeLabel: typesLabels.join(', ')
							};
							
							return coactivite;
						}
					)
					
					return {
						command: command,
						coactivites: newCoactivites,
					};
				}
			)
		).subscribe(
			({ command, coactivites }) => {
				this.controleSecurite = command.controleSecurite ?? null;
				this.decisionSubject = command.decisionSubject ? command.decisionSubject : null;

				if (coactivites.length === 0) {
					// S'il n'y a pas de coactivite, on obtient le meme resultat que si on affichait un message et que l'utilisateur cliquait sur "Valider"
					this.onDecision(command.accesId, CoactiviteDecision.AUTHORIZE);
					this.decisionSubject = null;
					return;
				}
				
				this.isCac = command.isUserCac;
				this.isCc = command.isUserCc;
							
				// Sinon on enregistre les informations pour pouvoir afficher le bon message, et on affiche la boite de dialogue
				this.accesId = command.accesId;
				this.coactivites = coactivites;
				this.displaySubject.next(true);
			}
		);
		
		// Quand on masque la fenetre de dialogue, on nettoie les donnees passees
		this.displaySubject.pipe(
			filter(show => !show)
		).subscribe(
			(_show) => {
				this.coactivites = [];
				this.decisionSubject = null;
			}
		)
	}
	
	@Input() id = 'coactiviteCheckerDialog';
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private checkCommandsSubject = new SubjectRelay<CoactiviteEolienCheckCommand>();
	@Input() set checkCommands(obs: Observable<CoactiviteEolienCheckCommand> | undefined) {
		this.checkCommandsSubject.registerSource(obs);
	}

	// Evenements de validation de l'operation en cours
	@Output() decisionIssued = new EventEmitter<[number, CoactiviteDecision, number | null]>();
	
	onAuthorizeRequested() {
		if (this.controleSecuriteCoactiviteId !== null) {
			this.controleSecurite = this.controleSecuriteCoactiviteId;
		}
		this.onDecision(this.accesId, CoactiviteDecision.AUTHORIZE_WITH_FORCE);
	}

	onDenyRequested() {
		this.onDecision(this.accesId, CoactiviteDecision.DENY);
	}

	onCancelRequested() {
		this.onDecision(this.accesId, CoactiviteDecision.CANCEL);
	}

	onValidationRequested() {
		this.onDecision(this.accesId, CoactiviteDecision.ASK_VALIDATION);
	}
	
	private onDecision(accesId: number, decision: CoactiviteDecision) {
	    this.decisionIssued.emit([accesId, decision, this.controleSecurite]);
	    if (this.decisionSubject) {
	        this.decisionSubject.next(decision);
	    }
	    this.displaySubject.next(false);
	}
}